"use client";

import React from "react";
import { PhoneMissed, Clock, ServerCrash, CircleAlert } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const timelineData = [{
  title: "The £500 Silence",
  description: "Every unanswered ring is a lost contract. In the UK trade, availability is your primary currency. If you don't pick up within three rings, they've already moved to the next professional on Google.",
  icon: PhoneMissed,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615185/img/uk_trades_missed_call_consequence.png"
}, {
  title: "The Midnight Invoice",
  description: "Spending your evenings chasing payments and typing quotes? Manual admin is stealing your family time and slowing down your cash flow. If you aren't automated, you're working for free after hours.",
  icon: Clock,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615185/img/uk_trades_late_night_paperwork.png"
}, {
  title: "The Invisible Pro",
  description: "You're the best at what you do, but if you're not on the first page of the local map, you don't exist. Poor visibility is costing you the premium local contracts that go to 'no-mark' competitors.",
  icon: ServerCrash,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1615185/img/uk_trades_local_map_ranking_gap.png"
}];
export default function TheCostOfInaction() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll();
  return <section data-section-id="52820" id="the-high-cost-of-silence" className="dark bg-background text-foreground py-24 relative overflow-hidden radiating-pattern">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-background/50 rounded-full blur-[100px] z-10" />
      </div>

      <div className="container mx-auto px-6 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          {}
          <div className="lg:col-span-5 relative">
            <div className="lg:sticky lg:top-32">
              <div ref={headerRef} className={cn("transition-all duration-1000 ease-out transform", headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
                <div className="flex items-center gap-3 mb-6 text-primary">
                  <CircleAlert className="w-6 h-6" />
                  <span className="text-sm font-mono uppercase tracking-widest">
                    Revenue Alert
                  </span>
                </div>
                <h2 className="h2 text-5xl md:text-6xl text-white mb-8">
                  The High Cost of <br />
                  <span className="text-primary">Silence</span>
                </h2>
                <p className="body-text text-muted-foreground max-w-md">
                  Manual admin and missed calls are silently draining your profits. See how traditional methods are stifling your growth and letting motivated leads slip through the cracks.
                </p>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 flex flex-col gap-24 relative">
            {}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-border/20 hidden md:block" />

            {timelineData.map((item, index) => <TimelineItem key={index} item={item} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}
function TimelineItem({
  item,
  index
}: {
  item: (typeof timelineData)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.4
  });
  return <div ref={ref} data-index={index} className={cn("relative pl-0 md:pl-24 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-20 translate-y-24")}>
      {}
      <div className={cn("absolute left-0 top-0 w-16 h-16 rounded-md border border-border/50 bg-card z-10 hidden md:flex items-center justify-center transition-all duration-700 delay-300", isVisible ? "border-primary shadow-[0_0_30px_-5px_hsl(var(--primary)/0.4)]" : "border-border/20")}>
        <item.icon className={cn("w-6 h-6 transition-colors duration-500", isVisible ? "text-primary" : "text-muted-foreground/20")} />
      </div>

      {}
      <Card className="glass-panel overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
        <CardContent className="p-0">
          <div className="flex flex-col gap-6">
            {}
            <div className="flex items-center gap-4 md:hidden mb-2">
              <div className={cn("w-12 h-12 rounded-md border bg-card flex items-center justify-center transition-colors duration-500", isVisible ? "border-primary text-primary shadow-[0_0_15px_-3px_hsl(var(--primary)/0.3)]" : "border-border/20 text-muted-foreground/20")}>
                <item.icon className="w-5 h-5" />
              </div>
              <h3 className={cn("h3 text-2xl transition-colors duration-500", isVisible ? "text-primary" : "text-muted-foreground")}>
                {item.title}
              </h3>
            </div>

            {}
            <h3 className={cn("h3 text-3xl hidden md:block transition-colors duration-500", isVisible ? "text-primary" : "text-muted-foreground")}>
              {item.title}
            </h3>

            {}
            <div className="relative aspect-video w-full overflow-hidden rounded-md border border-white/5 bg-secondary/20">
              <div className="absolute inset-0 bg-primary/10 z-10 mix-blend-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <img src={item.image} alt={item.title} className="w-full h-full object-cover grayscale contrast-125 brightness-75 group-hover:scale-105 transition-transform duration-1000 ease-out" />
              {}
              <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none" />
            </div>

            {}
            <p className="body-text text-muted-foreground border-l-2 border-primary/20 pl-6">
              {item.description}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>;
}